<!-- TextReader.vue — v16 -->
<!-- Leitor de texto: cópia enxuta de RightArea.vue.                              -->
<!-- Diferenças em relação a RightArea/TextDisplayer:                             -->
<!--  • NÃO renderiza NoteSection.vue (sem sidebar de notas).                     -->
<!--  • NÃO usa TextDisplayer.vue — o markup essencial dele foi incorporado aqui. -->
<!--  • Removidos TODOS os botões de controle e o título da atividade.            -->
<!--  • Mantém apenas os metadados do TEXTO + os cards Minhas / Outras / Total.   -->
<!--  • v2: metadados de Texto e Autor viram links (TextCardModal/AuthorCardModal).-->
<!--  • v3: o nome do TEXTO sai do grid e vira "main-title" (linha própria),      -->
<!--    igual ao nome da atividade no TextDisplayer; metadados renderizados abaixo.-->
<!--  • v4: Categoria vira link (CategoryCardModal) e cada Assunto vira um link    -->
<!--    individual (TopicCardModal), usando topicIds[idx] ↔ topicNames[idx].      -->
<!--  • v5: dois botões acima do painel de leitura, alinhados à direita —         -->
<!--    "Ver atividades" (cinza, btn-batch) e "Adicionar atividades" (roxo).      -->
<!--  • v6: "Adicionar atividades" abre o AddActivityForm IN-PLACE, no mesmo      -->
<!--    padrão do ActivityForm (contexto via store + guard memberCanCreateActivity).-->
<!--  • v7: após criar a atividade (@refresh), redireciona para o ActivityForm.   -->
<!--  • v8: ordem dos botões invertida (Adicionar antes de Ver) + cabeçalho       -->
<!--    "Texto" com linha horizontal full-width acima do nome do texto.           -->
<!--  • v9: rótulo "Texto" em preto, capitalização normal e alinhado à esquerda.  -->
<!--  • v10: removida a barra de rolagem interna da área de leitura; rolagem      -->
<!--    passa a ser GERAL no TextReader.                                          -->
<!--  • v11: font-size do rótulo "Texto" do cabeçalho ajustado para 1.5rem (h2).  -->
<!--  • v12: a barra de rolagem geral gruda no limite direito do container        -->
<!--    (margin-right negativo anula o padding do root) e ganha respiro entre o   -->
<!--    conteúdo (card Total) e a barra (padding-right).                          -->
<!--  • v13: cards Minhas/Outras/Total movidos para o cabeçalho, à direita da     -->
<!--    inscrição "Texto"; metadados do texto passam a seguir o layout do         -->
<!--    TextDisplayer: card agrupado, chips abaixo do subtítulo e campos em linhas.-->
<!--  • v14: cards do cabeçalho recebem ícones e nomes completos; seção de texto  -->
<!--    perde borda/sombra/fundo destacado, ficando integrada ao fundo do painel. -->
<!--  • v15: metadados deixam de usar ellipsis; textos longos agora quebram linha. -->
<!--  • v16: cards Minhas notas / Notas de outros deixam de ser placeholders e    -->
<!--    passam a ler myNotes e otherNotes de route.query, enviados pelo           -->
<!--    goToTextReader do DashboardView. Invariante: myNotes + otherNotes = total.-->

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";

import TextWithNotes from "@/components/Displayer/TextWithNotes.vue";
import TextWithNotesHtml from "@/components/Displayer/TextWithNotesHtml.vue";

import TextCardModal from "@/components/Text/TextCardModal.vue";
import AuthorCardModal from "@/components/Author/AuthorCardModal.vue";
import CategoryCardModal from "@/components/Category/CategoryCardModal.vue";
import TopicCardModal from "@/components/Topic/TopicCardModal.vue";
import AddActivityForm from "@/components/Activity/AddActivityForm.vue";

const route = useRoute();
const router = useRouter();

const textStore = useTextStore();
const userStore = useUserStore();
const activityStore = useActivityStore();
const managedAccountStore = useManagedAccountStore();

// Qual renderizador montar, pela mesma regra do TextDisplayer: o padrão é texto
// puro, para que texto ainda não convertido — ou vindo de uma API sem o campo —
// continue se comportando como hoje.
const isPlaintextContent = computed(
  () => (textStore.selectedText?.contentFormat ?? "plaintext") !== "html"
);

const totalNotes = computed(() => {
  const n = parseInt(route.query.noteCount, 10);
  return !isNaN(n) && n >= 0 ? n : 0;
});

const myNotes = computed(() => {
  const n = parseInt(route.query.myNotes, 10);
  return !isNaN(n) && n >= 0 ? n : 0;
});

const otherNotes = computed(() => {
  const n = parseInt(route.query.otherNotes, 10);
  return !isNaN(n) && n >= 0 ? n : 0;
});

const hasTopics = computed(
  () =>
    Array.isArray(textStore.selectedText?.topicNames) &&
    textStore.selectedText.topicNames.length > 0
);

const memberActivityPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.activities ?? null
);

const memberCanCreateActivity = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.create
);

function goToActivities() {
  const id = textStore.idText ?? textStore.selectedText?.textId;
  if (!id) return;

  const textTitle = textStore.selectedText?.title ?? "";

  router.push({
    name: "ActivityForm",
    params: { textId: String(id), textTitle },
  });
}

const showAddModal = ref(false);

function openAddActivity() {
  if (!memberCanCreateActivity.value) return;

  const id = textStore.idText ?? textStore.selectedText?.textId;
  if (!id) return;

  textStore.setIdText(id);
  activityStore.setCurrentTextId(id);

  showAddModal.value = true;
}

function handleActivityAdded() {
  showAddModal.value = false;
  goToActivities();
}

const showTextCardModal = ref(false);

function openTextCard() {
  showTextCardModal.value = true;
}

function closeTextCard() {
  showTextCardModal.value = false;
}

const showAuthorCardModal = ref(false);
const selectedAuthorId = ref(null);

function openAuthorCard(authorId) {
  if (!authorId) return;

  selectedAuthorId.value = authorId;
  showAuthorCardModal.value = true;
}

function closeAuthorCard() {
  showAuthorCardModal.value = false;
  selectedAuthorId.value = null;
}

const showCategoryCardModal = ref(false);
const selectedCategoryId = ref(null);

function openCategoryCard(categoryId) {
  if (!categoryId && categoryId !== 0) return;

  selectedCategoryId.value = Number(categoryId);
  showCategoryCardModal.value = true;
}

function closeCategoryCard() {
  showCategoryCardModal.value = false;
  selectedCategoryId.value = null;
}

const showTopicCardModal = ref(false);
const selectedTopicId = ref(null);

function openTopicCard(topicId) {
  if (topicId == null) return;

  selectedTopicId.value = Number(topicId);
  showTopicCardModal.value = true;
}

function closeTopicCard() {
  showTopicCardModal.value = false;
  selectedTopicId.value = null;
}

onMounted(async () => {
  if (textStore.idText) {
    await textStore.getTextById(textStore.idText);
  }
});
</script>

<template>
  <main class="text-reader-main">
    <div v-if="textStore.selectedText" class="text-reader-root">
      <div class="reader-header">
        <div class="reader-header-bar">
          <span class="reader-header-label">Texto</span>

          <div class="header-stats" aria-label="Resumo de notas do texto">
            <span class="stat-chip stat-chip--mine" title="Minhas notas">
              <i class="fas fa-user"></i>
              <span class="stat-chip-label">Minhas notas</span>
              <span class="stat-chip-value">{{ myNotes }}</span>
            </span>

            <span class="stat-chip stat-chip--others" title="Nota de outros">
              <i class="fas fa-users"></i>
              <span class="stat-chip-label">Nota de outros</span>
              <span class="stat-chip-value">{{ otherNotes }}</span>
            </span>

            <span class="stat-chip stat-chip--total" title="Total">
              <i class="fas fa-layer-group"></i>
              <span class="stat-chip-label">Total</span>
              <span class="stat-chip-value">{{ totalNotes }}</span>
            </span>
          </div>
        </div>

        <hr class="reader-header-rule" />
      </div>

      <div class="reader-scroll text-scroll-container">
        <section class="text-meta-card" aria-label="Detalhes do texto">
          <div class="text-title-group">
            <button
              type="button"
              class="text-name link-btn"
              :title="textStore.selectedText.title"
              @click="openTextCard"
            >
              {{ textStore.selectedText.title }}
            </button>

            <div v-if="textStore.selectedText.subtitle" class="text-subtitle">
              {{ textStore.selectedText.subtitle }}
            </div>

            <div v-if="hasTopics" class="topics-under-subtitle">
              <div class="topic-chips">
                <button
                  v-for="(name, idx) in textStore.selectedText.topicNames"
                  :key="idx"
                  type="button"
                  class="topic-chip"
                  :title="`Abrir assunto: ${name}`"
                  @click="openTopicCard(textStore.selectedText.topicIds?.[idx])"
                >
                  {{ name }}
                </button>
              </div>
            </div>
          </div>

          <div class="text-meta-divider" aria-hidden="true"></div>

          <div class="meta-fields">
            <div class="meta-field">
              <span class="meta-label">Autor</span>
              <button
                v-if="textStore.selectedText.authorId"
                type="button"
                class="link-btn meta-value"
                @click="openAuthorCard(textStore.selectedText?.authorId)"
                :title="textStore.selectedText.authorName"
              >
                {{ textStore.selectedText.authorName }}
              </button>
              <span v-else class="meta-value meta-value--empty">Não informado</span>
            </div>

            <div class="meta-field">
              <span class="meta-label">Categoria</span>
              <button
                v-if="textStore.selectedText.categoryId"
                type="button"
                class="link-btn meta-value"
                @click="openCategoryCard(textStore.selectedText?.categoryId)"
                :title="textStore.selectedText.categoryName"
              >
                {{ textStore.selectedText.categoryName }}
              </button>
              <span v-else class="meta-value meta-value--empty">Não informado</span>
            </div>

            <div class="meta-field">
              <span class="meta-label">Fonte</span>
              <span
                class="meta-value"
                :title="textStore.selectedText.source || 'fonte não informada'"
              >
                {{ textStore.selectedText.source || "não informada" }}
              </span>
            </div>

            <div class="meta-field meta-field--muted">
              <span class="meta-label">Tamanho</span>
              <span
                class="meta-value"
                :title="`${textStore.selectedText.textLength} caracteres`"
              >
                {{ textStore.selectedText.textLength }} caracteres
              </span>
            </div>
          </div>
        </section>

        <div class="reader-actions">
          <button
            v-if="memberCanCreateActivity"
            type="button"
            class="btn-primary"
            @click="openAddActivity"
          >
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar atividades
          </button>

          <button type="button" class="btn btn-batch" @click="goToActivities">
            Ver atividades
          </button>
        </div>

        <div class="text-card">
          <div class="text-reading-area">
            <!--
              Dois renderizadores, um por formato — o mesmo arranjo do
              TextDisplayer, inclusive o invólucro.

              O <p class="text-body"> com white-space: pre-wrap vale só no ramo
              de texto puro: é ele que transforma os \n em quebra visível. No
              ramo HTML seria desastre — <p> dentro de <p> faz o navegador
              remontar a árvore, e <figure> e <table> nem cabem ali.
            -->
            <p v-if="isPlaintextContent" class="text-body">
              <TextWithNotes
                v-if="userStore.currentUser"
                :content="textStore.fileContent"
                :notes="[]"
                :userId="userStore.currentUser?.userId"
                :createdByUserId="textStore.selectedText?.createdByUserId"
                :updatedByUserId="textStore.selectedText?.updatedByUserId"
                :hasExponent="false"
                :hasHighlights="false"
                :isMultiSelectMode="false"
              />
            </p>

            <div v-else class="text-body text-body--html">
              <TextWithNotesHtml
                v-if="userStore.currentUser"
                :content="textStore.fileContent"
                :notes="[]"
                :userId="userStore.currentUser?.userId"
                :createdByUserId="textStore.selectedText?.createdByUserId"
                :updatedByUserId="textStore.selectedText?.updatedByUserId"
                :hasExponent="false"
                :hasHighlights="false"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>Carregando...</div>

    <Teleport to="body">
      <TextCardModal
        v-if="showTextCardModal"
        :show="showTextCardModal"
        :textId="textStore.idText"
        @close="closeTextCard"
      />

      <AuthorCardModal
        v-if="showAuthorCardModal"
        :show="showAuthorCardModal"
        :authorId="selectedAuthorId"
        @close="closeAuthorCard"
      />

      <CategoryCardModal
        v-if="showCategoryCardModal && selectedCategoryId"
        :show="showCategoryCardModal"
        :categoryId="selectedCategoryId"
        @close="closeCategoryCard"
      />

      <TopicCardModal
        v-if="showTopicCardModal && selectedTopicId"
        :show="showTopicCardModal"
        :topicId="selectedTopicId"
        @close="closeTopicCard"
      />

      <AddActivityForm
        v-if="showAddModal && memberCanCreateActivity"
        @close="showAddModal = false"
        @refresh="handleActivityAdded"
      />
    </Teleport>
  </main>
</template>

<style scoped>
/* ===== Raiz / layout (sem sidebar) ===== */
.text-reader-main {
  background-color: var(--bg-content);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
}

.text-reader-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 8px;
  border-radius: 16px;
  background: var(--bg-header);
  overflow: hidden;
}

.reader-header {
  flex-shrink: 0;
}

.reader-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.reader-header-label {
  display: block;
  text-align: left;
  padding-left: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
}

.reader-header-rule {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 6px -8px 12px;
}

/* ===== Cards reduzidos no cabeçalho ===== */
.header-stats {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
  cursor: default;
}

.stat-chip i {
  font-size: 0.72rem;
  opacity: 0.85;
}

.stat-chip-label {
  font-size: 0.7rem;
  font-weight: 700;
  opacity: 0.85;
}

.stat-chip-value {
  font-size: 0.82rem;
  font-weight: 800;
}

.stat-chip--mine {
  background-color: var(--brand-tint);
  color: #4f46e5;
  border-color: var(--brand-soft);
}

.stat-chip--others {
  background-color: #fff4e6;
  color: #b45309;
  border-color: #ffe8cc;
}

.stat-chip--total {
  background-color: #ece7ff;
  color: #4b3f86;
  border-color: #ded5ff;
}

.reader-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-right: -8px;
  padding-right: 8px;
}

/* ===== Card de metadados do texto — destacado ===== */
.text-meta-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
  flex-shrink: 0;
}

.text-title-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.text-name {
  align-self: flex-start;
  max-width: 100%;
  font-size: 0.95rem;
  font-weight: 600;
  color: #c0842f;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.text-subtitle {
  max-width: 100%;
  font-size: 0.82rem;
  color: var(--text-light);
  font-style: italic;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.topics-under-subtitle {
  display: flex;
  align-items: center;
  margin-top: 6px;
  min-width: 0;
}

.topic-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.topic-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.5;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  background-color: var(--brand-tint);
  color: #4f46e5;
  border: 1px solid var(--brand-soft);
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.05s ease;
}

.topic-chip:hover {
  background-color: var(--brand-soft);
}

.topic-chip:active {
  transform: translateY(1px);
}

.text-meta-divider {
  height: 1px;
  width: 100%;
  margin: 4px 0;
  background: var(--border-color);
  opacity: 0.85;
}

.meta-fields {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  margin-top: 0;
}

.meta-field {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  font-size: 0.82rem;
  color: var(--text-light);
  min-width: 0;
}

/* Catalogação ainda em aberto (o texto pode nascer só com título e conteúdo):
   o rótulo entra apagado, e sem card para abrir não há botão. */
.meta-value--empty {
  opacity: 0.6;
  font-style: italic;
}

.meta-label {
  flex-shrink: 0;
  min-width: 72px;
}

.meta-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.meta-field--muted .meta-label,
.meta-field--muted .meta-value {
  opacity: 0.65;
}

/* ===== Links ===== */
.link-btn {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.link-btn:hover {
  text-decoration: underline;
}

.link-btn.text-name {
  font-weight: 700;
}

/* Autor/Categoria: o clique deve valer só para o nome, não pra linha inteira
   (sem isso, o button herda flex:1 1 auto de .meta-value e estica até a borda). */
.link-btn.meta-value {
  flex: 0 1 auto;
  width: fit-content;
}

/* ===== Ações acima do painel de leitura ===== */
.reader-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin: 10px 0 2px;
  flex-shrink: 0;
}

/* ===== "folha" de leitura ===== */
.text-card {
  background-color: var(--bg-main);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  margin-top: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
  padding-top: 12px;
}

.text-reading-area {
  flex: 1 0 auto;
  overflow: visible;
  padding: 6px 10px 24px 10px;
  width: 100%;
}

.text-body {
  line-height: 1.55;
  font-size: 1.05rem;
  max-width: 760px;
  margin: 0 auto;
  text-align: left;
  white-space: pre-wrap;
}
</style>